import { Delete } from 'lucide-react';
import { useEffect } from 'react';
import { cn } from '@/lib/utils';

export interface PinPadProps {
  value: string;
  onChange: (value: string) => void;
  /** Called with the PIN when "OK" (or Enter) is pressed with at least `minLength` digits. */
  onSubmit?: (value: string) => void;
  minLength?: number;
  maxLength?: number;
  disabled?: boolean;
  /** Red dots (wrong PIN). */
  invalid?: boolean;
  /** Also accept digits, Backspace and Enter typed anywhere on the page (lock screen). */
  captureKeyboard?: boolean;
  label?: string;
  className?: string;
}

const KEY_CLASS =
  'flex h-16 select-none items-center justify-center rounded-full border border-border bg-background text-2xl font-semibold tabular-nums text-foreground active:bg-accent disabled:opacity-40';

/** PIN keypad with masked dots (lock screen, PIN setup). 4–6 digits by default. */
export function PinPad({
  value,
  onChange,
  onSubmit,
  minLength = 4,
  maxLength = 6,
  disabled = false,
  invalid = false,
  captureKeyboard = false,
  label = 'PIN',
  className,
}: PinPadProps) {
  const canSubmit = !disabled && value.length >= minLength;
  const press = (digit: string) => {
    if (disabled || value.length >= maxLength) return;
    onChange(value + digit);
  };
  const back = () => {
    if (!disabled) onChange(value.slice(0, -1));
  };
  const submit = () => {
    if (canSubmit) onSubmit?.(value);
  };

  useEffect(() => {
    if (!captureKeyboard) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      if (/^\d$/.test(event.key)) {
        event.preventDefault();
        press(event.key);
      } else if (event.key === 'Backspace') {
        event.preventDefault();
        back();
      } else if (event.key === 'Enter') {
        event.preventDefault();
        submit();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  return (
    <div className={cn('flex flex-col items-center gap-5', className)}>
      <div
        className="flex h-6 items-center gap-3"
        role="img"
        aria-label={`${label}: ${value.length} cifre inserite`}
      >
        {Array.from({ length: Math.max(minLength, Math.min(maxLength, value.length + 1)) }).map(
          (_, i) => (
            <span
              key={i}
              className={cn(
                'size-4 rounded-full border-2',
                invalid
                  ? 'border-danger bg-danger'
                  : i < value.length
                    ? 'border-primary bg-primary'
                    : 'border-input',
              )}
            />
          ),
        )}
      </div>
      <div
        className="grid w-full max-w-72 grid-cols-3 gap-3"
        role="group"
        aria-label="Tastierino PIN"
      >
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
          <button
            key={d}
            type="button"
            className={KEY_CLASS}
            disabled={disabled}
            onClick={() => press(d)}
          >
            {d}
          </button>
        ))}
        <button
          type="button"
          className={KEY_CLASS}
          aria-label="Cancella cifra"
          disabled={disabled || value.length === 0}
          onClick={back}
        >
          <Delete className="size-7" />
        </button>
        <button type="button" className={KEY_CLASS} disabled={disabled} onClick={() => press('0')}>
          0
        </button>
        <button
          type="button"
          className={cn(
            KEY_CLASS,
            'border-primary bg-primary text-lg text-primary-foreground active:bg-primary-hover',
          )}
          disabled={!canSubmit}
          onClick={submit}
        >
          OK
        </button>
      </div>
    </div>
  );
}
